// src/encounter/services/use-assign-care-plan.ts
// [PROD-3206] Data layer for the physician's "Assign care plan": the care-model list and the C2 enrol.
// Mounted only behind `?x=assign-care-plan`; the list is read only while the dialog is open.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CareModelOption } from '../lib/care-model-options';
import type { CareModelEnrolmentWire } from '@/types/care-model-api';
import { enrolInCareModel, fetchCareModels } from './care-model-api';
import { carePlansKey, patientEnrolmentsKey } from './use-care-plans';

export const CARE_MODEL_OPTIONS_KEY = ['care-model-options'] as const;

export function useCareModelOptions(enabled: boolean) {
  return useQuery<CareModelOption[]>({
    queryKey: CARE_MODEL_OPTIONS_KEY,
    enabled,
    queryFn: ({ signal }) => fetchCareModels(signal),
  });
}

/** C2. A new enrolment refreshes the patient's care plans (C5) and enrolments (C4) so the readout shows it. */
export function useAssignCarePlan(patientId: string) {
  const qc = useQueryClient();
  return useMutation<CareModelEnrolmentWire, unknown, string>({
    mutationFn: (definitionId) => enrolInCareModel(definitionId, patientId),
    onSuccess: (data) => {
      if (data.alreadyEnrolled) return;
      void qc.invalidateQueries({ queryKey: carePlansKey(patientId) });
      void qc.invalidateQueries({ queryKey: patientEnrolmentsKey(patientId) });
    },
  });
}
